import clsx from 'clsx'
import { useState } from 'react'

import { Icon } from '@wartek-id/icon-v2'
import { Text } from '@wartek-id/text-v1'
import { Button } from '@wartek-id/button-v1'

export type BannerProps = {
  hideIcon?: boolean
  className?: string
  variant?: 'warning' | 'info'
  dismissible?: boolean
  children?: React.ReactNode
}

type BannerVariant = 'warning' | 'info'
const STYLES: Record<BannerVariant, string> = {
  warning: 'bg-surface-warning-subdued border border-warning',
  info: 'bg-cyan-5 border border-blue-70',
}
const ICONS: Record<BannerVariant, string> = {
  warning: 'warning',
  info: 'info',
}

function Banner({
  hideIcon,
  className,
  variant = 'info',
  children,
  dismissible,
}: BannerProps) {
  const [isShown, setIsShown] = useState(true)

  if (!isShown) return null

  return (
    <div
      className={clsx(
        'relative',
        'flex p-4 gap-3',
        'rounded-md',
        STYLES[variant],
        dismissible && 'pr-12',
        className
      )}
      role="alert"
    >
      {!hideIcon && (
        <Icon className="!text-2xl !leading-6">{ICONS[variant]}</Icon>
      )}
      <div className="space-y-3">{children}</div>
      {dismissible && (
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setIsShown(false)}
          aria-label="Tutup banner"
          className="right-4 top-3.5 absolute"
          iconOnly
        >
          <Icon fontSize="sm" className="!text-xl !leading-5">
            close
          </Icon>
        </Button>
      )}
    </div>
  )
}

function BannerTitle({
  children,
  ...props
}: React.ComponentProps<typeof Text>) {
  return (
    <Text variant="heading-sm" {...props}>
      {children}
    </Text>
  )
}

function BannerDescription({
  children,
  ...props
}: React.ComponentProps<typeof Text>) {
  return (
    <Text variant="body" {...props}>
      {children}
    </Text>
  )
}

export { Banner, BannerTitle, BannerDescription }
